import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, useUser } from '@clerk/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { apiWorker, syncUserWithBackend, setGlobalGetToken } from '../api/worker';
import { SHOULD_USE_MOCKS, CLERK_PUBLISHABLE_KEY } from '../lib/clerk';
import { useBackendHealth } from '../hooks/useBackendHealth';
import { Capability } from '../types';

interface Membership {
  id: string; // The Node ID
  nodeId: string;
  nodeName: string;
  nodeSlug: string;
  role: string;
  unreadCount: number;
  tier: 'LOCAL' | 'FOUNDATION' | 'MOMENTUM' | 'INSTITUTIONAL';
  parentNodeId?: string;
  capabilities: Capability[];
}

interface AuthContextType {
  user: any;
  memberships: Membership[];
  isLoading: boolean;
  isSignedIn: boolean;
  isBackendSynced: boolean;
  isSyncSkipped: boolean;
  error: Error | null;
  signOut: () => Promise<void>;
  hardReset: () => Promise<void>;
  getToken: (options?: { template?: string }) => Promise<string | null>;
  loginAs: (user: any) => Promise<void>;
  isSyncing: boolean;
  isSyncFailed: boolean;
  isMaintenanceMode: boolean;
  isCheckingHealth: boolean;
  retrySync: (isBackground?: boolean) => Promise<void>;
  checkHealth: () => Promise<void>;
  skipSync: () => void;
  enableGodMode: () => void;
  disableGodMode: () => void;
  isGodMode: boolean;
}

const AuthCtx = createContext<AuthContextType | null>(null);

const MockAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeMockUser, setActiveMockUser] = useState(() => {
    const saved = localStorage.getItem('mock_user_identity');
    return saved ? JSON.parse(saved) : null;
  });
  
  const [isGodMode, setIsGodMode] = useState(() => import.meta.env.DEV && localStorage.getItem('GOD_MODE_ENABLED') === 'true');

  const enableGodMode = useCallback(() => {
    localStorage.setItem('GOD_MODE_ENABLED', 'true');
    setIsGodMode(true);
    toast.success('God Mode Enabled');
    setTimeout(() => window.location.reload(), 500);
  }, []);

  const disableGodMode = useCallback(() => {
    localStorage.removeItem('GOD_MODE_ENABLED');
    setIsGodMode(false);
    toast.success('God Mode Disabled');
    setTimeout(() => window.location.reload(), 500);
  }, []);

  const loginAs = useCallback(async (user: any) => {
    localStorage.setItem('mock_user_identity', JSON.stringify(user));
    setActiveMockUser(user);
    toast.success(`Signed in as ${user.fullName}`);
    window.location.href = '/dashboard';
  }, []);

  useEffect(() => {
    const handleStorage = () => {
      const saved = localStorage.getItem('mock_user_identity');
      if (saved) setActiveMockUser(JSON.parse(saved));
    };
    window.addEventListener('storage', handleStorage);
    const interval = setInterval(handleStorage, 1000);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  const { data: rawMemberships, isLoading: isMembershipsLoading, error: membershipsError } = useQuery({
    queryKey: ['user-memberships-mock', activeMockUser?.id],
    queryFn: () => apiWorker.get('/api/users/me/memberships').then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : (Array.isArray(r.data) ? r.data : [])),
    enabled: !!activeMockUser,
    staleTime: 5 * 60 * 1000
  });

  const queryClient = useQueryClient();

  const signOut = useCallback(async () => {
    queryClient.clear();
    
    const logoutReason = sessionStorage.getItem('logout_reason');
    sessionStorage.clear();
    if (logoutReason) sessionStorage.setItem('logout_reason', logoutReason);
    
    localStorage.removeItem('mock_user_identity');
    setActiveMockUser(null);
    window.location.href = '/';
  }, [queryClient]);

  const memberships = Array.isArray(rawMemberships) ? rawMemberships : [];
  const error = (membershipsError as Error) || null;

  const value = React.useMemo(() => ({
    user: activeMockUser,
    memberships,
    isLoading: isMembershipsLoading,
    isSignedIn: !!activeMockUser,
    isBackendSynced: !!activeMockUser,
    isSyncSkipped: false,
    error: membershipsError as Error | null,
    signOut,
    hardReset: async () => {
      localStorage.clear();
      sessionStorage.clear();
      queryClient.clear();
      window.location.href = '/';
    },
    getToken: async () => 'mock-token',
    loginAs,
    isSyncing: false,
    isSyncFailed: false,
    isMaintenanceMode: false,
    isCheckingHealth: false,
    retrySync: async (isBackground?: boolean) => {},
    checkHealth: async () => {},
    skipSync: () => {},
    enableGodMode,
    disableGodMode,
    isGodMode
  }), [activeMockUser, memberships, isMembershipsLoading, membershipsError, signOut, loginAs, isGodMode, enableGodMode, disableGodMode, queryClient]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

const LiveAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoaded: isAuthLoaded, isSignedIn: isClerkSignedIn, signOut: clerkSignOut, getToken } = useAuth();
  const { user: clerkUser, isLoaded: isUserLoaded } = useUser();
  const queryClient = useQueryClient();
  const [syncedUserId, setSyncedUserId] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const isSyncingRef = useRef(false);
  const [isSyncFailed, setIsSyncFailed] = useState(false);
  const [isSyncSkipped, setIsSyncSkipped] = useState(false);
  const [isEmailConflict, setIsEmailConflict] = useState(false);
  const [isGodMode, setIsGodMode] = useState(() => import.meta.env.DEV && localStorage.getItem('GOD_MODE_ENABLED') === 'true');
  const [forceNotLoading, setForceNotLoading] = useState(false);
  const [stuckLoadingTimeout, setStuckLoadingTimeout] = useState(false);
  const { isMaintenanceMode: isBackendDown, consecutiveFailures: _consecutiveFailures, isChecking: isCheckingHealth, refresh: refreshHealth } = useBackendHealth();

  useEffect(() => {
    if (isAuthLoaded) {
      console.log(`[Auth Context Status] isAuthLoaded: ${isAuthLoaded}, isUserLoaded: ${isUserLoaded}, isClerkSignedIn: ${isClerkSignedIn}, clerkUser: ${clerkUser?.id || 'NULL'}`);
    }
  }, [isAuthLoaded, isUserLoaded, isClerkSignedIn, clerkUser]);

  useEffect(() => {
    if (getToken) {
      setGlobalGetToken(getToken as any);
    }
    return () => {
      setGlobalGetToken(null);
    };
  }, [getToken]);

  // Derive maintenance mode directly from health check state — no cascading setState needed
  const isMaintenanceMode = isBackendDown;

  useEffect(() => {
    const handleUnauthorized = async () => {
      console.warn("Received auth:unauthorized event. Signing out.");
      try {
        if (clerkSignOut) {
          await clerkSignOut();
        }
      } catch (e) {
        console.error(e);
      } finally {
        sessionStorage.setItem('auth_401_bounce', 'true');
        window.location.href = '/sign-in';
      }
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [clerkSignOut]);

  const performSync = useCallback(async (userToSync: any, isBackground: boolean = false) => {
    // Avoid redundant syncs or syncs while mock mode is active
    if (!userToSync?.id || SHOULD_USE_MOCKS) return;

    console.log(`[Auth] Starting sync for user ${userToSync.id}. Background: ${isBackground}`);
    
    if (isSyncingRef.current && !isBackground) {
      console.log('[Auth] Foreground sync already in progress, skipping duplicate call');
      return;
    }

    if (!isBackground) {
      isSyncingRef.current = true;
      setIsSyncing(true);
    } else {
      // Unblock UI immediately for existing users, as the backend already knows them
      setSyncedUserId(userToSync.id);
    }
    setIsSyncFailed(false);
    
    console.log(`[Auth Sync] Preparing TO SYNC via API for ${userToSync.id}...`);
    
    // Safety timeout: if sync takes > 6s, network or backend is stalling
    const syncTimeout = setTimeout(() => {
      if (!isBackground) {
        console.warn(`[Auth] Sync TIMED OUT after 6s for ${userToSync.id} - Forcing state transition`);
        setIsSyncing(false);
        setIsSyncFailed(true);
        toast.error('Identity sync is taking longer than expected. Please check your connection.', { id: 'auth-sync-timeout' });
      }
    }, 6000);
    
    try {

      console.log(`[Auth Sync] TRIGGERING API POST to /api/users/me/sync for ${userToSync.id}`);
      const email = userToSync.primaryEmailAddress?.emailAddress 
        || (Array.isArray(userToSync.emailAddresses) && userToSync.emailAddresses[0]?.emailAddress)
        || userToSync.email;
      const firstName = userToSync.firstName 
        || (userToSync.username ? userToSync.username.split('_')[0] : '')
        || (email ? email.split('@')[0] : 'User');
      const lastName = userToSync.lastName || '';
      const result = await syncUserWithBackend({
        id: userToSync.id,
        email,
        firstName,
        lastName,
        imageUrl: userToSync.imageUrl || ''
      });

      clearTimeout(syncTimeout);
      console.log(`[Auth Sync] Success=${!!result} for ${userToSync.id}`);

      if (!result) {
        setIsSyncFailed(true);
      } else {
        // ONLY update the ref after success
        setSyncedUserId(userToSync.id);
        setIsEmailConflict(false);
        
        // Remember sync success for 24 hours to allow background sync on next visit
        localStorage.setItem(`auth_sync_${userToSync.id}`, Date.now().toString());
        
        // Invalidate queries to ensure fresh data after sync
        queryClient.invalidateQueries({ queryKey: ['user-memberships-live'] });
      }
    } catch (error: any) {
      clearTimeout(syncTimeout);
      console.error('[Auth] Critical failure during profile sync', error);
      // Log more details about the error if available
      if (error.response) {
        console.error('[Auth] Error response:', error.response.status, error.response.data);
      }
      setIsSyncFailed(true);
      
      // 409 Conflict: user email registered with a different Clerk account
      if (error.response?.status === 409) {
        setIsEmailConflict(true);
        toast.error("This email is already associated with another account. Please sign in with your original authentication provider.", {
          id: 'auth-sync-409',
          duration: 10000
        });
      } else if (!isBackground) {
        // Provide immediate feedback if this was a manual foreground retry
        toast.error('Sync failed. The server might be experiencing issues.', { id: 'auth-sync-error' });
      }
    } finally {
      console.log(`[Auth] Sync final state: isBackground=${isBackground}, id=${userToSync.id}`);
      if (!isBackground) {
        isSyncingRef.current = false;
        setIsSyncing(false);
      }
    }
  }, [queryClient]);

  // Trigger sync aggressively when we spot a divergence
  useEffect(() => {
    if (isAuthLoaded && isClerkSignedIn && isUserLoaded && clerkUser?.id) {
      if (syncedUserId !== clerkUser.id && !isSyncing && !isSyncFailed) {
        console.log(`[Auth Trigger] Condition MET for ${clerkUser.id}. Current ref=${syncedUserId}`);
        
        const lastSync = localStorage.getItem(`auth_sync_${clerkUser.id}`);
        const isBackground = !!lastSync && (Date.now() - parseInt(lastSync) < 7 * 24 * 60 * 60 * 1000);
        
        // Defer off the effect's synchronous path — the setStates inside performSync
        // land in a microtask in the same commit window with identical behavior.
        queueMicrotask(() => performSync(clerkUser, isBackground));
      } else if (isSyncing) {
        console.log(`[Auth Trigger] Skip: Already syncing ${clerkUser.id}`);
      }
    } else if (isAuthLoaded && !isClerkSignedIn && syncedUserId !== null) {
      console.log('[Auth Trigger] Clearing syncedUserId ref (unsigned state)');
      queueMicrotask(() => setSyncedUserId(null));
    }
  }, [isAuthLoaded, isClerkSignedIn, isUserLoaded, clerkUser, syncedUserId, isSyncing, isSyncFailed, performSync]);

  const retrySync = useCallback(async (isBackground: boolean = false) => {
    setIsEmailConflict(false);
    // Force a health check re-evaluation when manually retrying
    const isHealthy = await refreshHealth();
    
    if (!isHealthy) {
      if (!isBackground) toast.error('Backend still unreachable. Please try again later.', { id: 'health-check-fail' });
      return;
    }

    if (clerkUser) {
      await performSync(clerkUser, isBackground);
    } else {
      // If not signed in, just checking health is enough to allow clearing maintenance banner
      if (!isBackground) toast.success('Backend is back online.');
    }
  }, [clerkUser, performSync, refreshHealth]);

  const checkHealth = useCallback(async () => {
    await refreshHealth();
  }, [refreshHealth]);
  
  const skipSync = useCallback(() => {
    console.log('[Auth] Manually skipping sync - FORCING NOT LOADING (Sync not confirmed)');
    setForceNotLoading(true);
    setIsSyncSkipped(true);
    setIsSyncing(false);
    setIsSyncFailed(false); // Clear the error state so the red banner goes away
    setIsEmailConflict(false);
    // Force a small delay then success to let the UI update its loading state
    setTimeout(() => {
      toast.success('Bypassing profile sync for browsing. Organization creation requires full account sync.', { id: 'auth-skip' });
    }, 100);
  }, []);

  const enableGodMode = useCallback(() => {
    localStorage.setItem('GOD_MODE_ENABLED', 'true');
    setIsGodMode(true);
    toast.success('God Mode Enabled');
    setTimeout(() => window.location.reload(), 500);
  }, []);

  const disableGodMode = useCallback(() => {
    localStorage.removeItem('GOD_MODE_ENABLED');
    setIsGodMode(false);
    toast.success('God Mode Disabled');
    setTimeout(() => window.location.reload(), 500);
  }, []);

  const location = useLocation();

  // Check if we are on a public page to avoid blocking overlays
  const isPublicRoute = React.useMemo(() => {
    const publicRoutes = [
      '/', '/about', '/privacy', '/use-cases', '/mission', '/newsroom', 
      '/contact', '/terms', '/cookies', '/security', '/dpa', '/aup', '/dmca',
      '/sign-in', '/sign-up', '/global', '/unauthorized'
    ];
    const path = location.pathname.toLowerCase();
    const normalized = path.replace(/\/$/, '') || '/';
    return publicRoutes.some(route => normalized === route || path.startsWith(route + '/'));
  }, [location.pathname]);

  useEffect(() => {
    const handleOnline = () => {
      if (isSyncFailed && !isEmailConflict) {
        console.log('[Auth] Network reconnected. Attempting background sync...');
        retrySync(true);
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [isSyncFailed, isEmailConflict, retrySync]);

  const { data: rawMemberships, isLoading: isMembershipsLoading, error: membershipsError } = useQuery({
    queryKey: ['user-memberships-live', clerkUser?.id],
    queryFn: async () => {
      try {
        if (getToken) {
          const token = await getToken();
          if (!token && !SHOULD_USE_MOCKS) {
            console.warn('[Auth Query] Awaiting JWT bearer token before fetching nodes/memberships');
            return [];
          }
        }
        const [membershipsRes, nodesRes] = await Promise.all([
          apiWorker.get('/api/users/me/memberships'),
          apiWorker.get('/api/nodes/me?size=100').catch((err) => {
            console.error('[Auth Query] Failed to fetch nodes/me:', err);
            return { data: { content: [] } };
          })
        ]);
        
        const nodeMap = new Map();
        const nodesData = (nodesRes.data && Array.isArray(nodesRes.data.content)) ? nodesRes.data.content : (Array.isArray(nodesRes.data) ? nodesRes.data : []);
        nodesData.forEach((n: any) => nodeMap.set(n.id, n));

        const membershipsData = (membershipsRes.data && Array.isArray(membershipsRes.data.content)) ? membershipsRes.data.content : (Array.isArray(membershipsRes.data) ? membershipsRes.data : []);

        const validMemberships = membershipsData.filter((m: any) => {
          const node = nodeMap.get(m.nodeId);
          return !!node || (m.node && m.node.name) || (m.nodeName && m.nodeName !== 'Unknown Organization' && m.nodeSlug && m.nodeSlug !== 'unknown');
        });

        return validMemberships.map((m: any) => {
          const node = nodeMap.get(m.nodeId) || m.node;
          return {
            ...m,
            id: m.nodeId || m.id, // Ensure id is the nodeId for component compatibility
            membershipId: m.id, // Preserve actual membership id
            nodeName: node?.name || m.nodeName || 'Unknown Organization',
            nodeSlug: node?.slug || m.nodeSlug || 'unknown',
            tier: node?.tier || m.tier || 'LOCAL',
            parentNodeId: node?.parentId || m.parentNodeId,
            unreadCount: 0
          };
        });
      } catch (err: any) {
        if (err.response?.status === 404) return [];
        console.error('[Auth Query] Error fetching memberships:', err);
        throw err;
      }
    },
    enabled: !!isClerkSignedIn && isUserLoaded && !!clerkUser?.id && syncedUserId === clerkUser.id && !isSyncing && !isSyncFailed && !isEmailConflict,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      // Don't retry on auth, rate limit, or not found errors
      if (
        error?.response?.status === 401 || 
        error?.response?.status === 403 || 
        error?.response?.status === 429 ||
        error?.response?.status === 404
      ) return false;
      return failureCount < 2;
    }
  });

  useEffect(() => {
    if (membershipsError) {
      console.error('[Auth Error] Membership sync failed:', membershipsError);
      // Only toast on non-401/403 errors to avoid spamming the splash
      const status = (membershipsError as any)?.response?.status;
      if (status && status !== 401 && status !== 403 && status !== 404) {
        toast.error('Unable to verify group memberships. Using default access.');
      }
    }
  }, [membershipsError]);

  const memberships = Array.isArray(rawMemberships) ? rawMemberships : [];
  const error = (membershipsError as Error) || null;
  
  const clerkUserId = clerkUser?.id;
  
  // We consider the app "Loading" if:
  // 1. Clerk hasn't initialized its core state yet
  // 2. A user is signed in but we haven't loaded their profile or finished syncing
  // 3. We use an 8s timeout hack to prevent indefinite stalling

  const isLoading = !forceNotLoading && !stuckLoadingTimeout && (
    !isAuthLoaded || 
    (isClerkSignedIn && (
      !isUserLoaded || 
      isSyncing || 
      (!isSyncFailed && !isMaintenanceMode && !!clerkUserId && syncedUserId !== clerkUserId)
    ))
  );

  useEffect(() => {
    if (!isLoading) return;
    
    // If loading for > 4s, release UI block and notify user with actionable options
    const wait = setTimeout(() => {
      console.warn("[Auth] TIMEOUT: Force clearing loading state after 4s! (Failsafe)");
      setStuckLoadingTimeout(true);
      if (isClerkSignedIn && !syncedUserId) {
        setIsSyncFailed(true);
      }
    }, 4000);
    return () => clearTimeout(wait);
  }, [isLoading, isClerkSignedIn, syncedUserId]);

  useEffect(() => {
    if (isAuthLoaded) {
      const loadingReasons = [];
      if (!isAuthLoaded) loadingReasons.push('!isAuthLoaded');
      if (isClerkSignedIn && !isUserLoaded) loadingReasons.push('!isUserLoaded');
      if (isClerkSignedIn && !clerkUserId) loadingReasons.push('signedInButNoUserId');
      if (isClerkSignedIn && isSyncing) loadingReasons.push('isSyncing');
      if (isClerkSignedIn && !isSyncFailed && !isMaintenanceMode && !!clerkUserId && syncedUserId !== clerkUserId) loadingReasons.push('needsSync');

      console.log(`[Auth Context State Update] isLoading=${isLoading}, forceNotLoading=${forceNotLoading}, Reasons: ${loadingReasons.join(', ') || 'NONE'}`, {
        isAuthLoaded,
        isUserLoaded,
        isClerkSignedIn,
        hasUser: !!clerkUser,
        isSyncing,
        isSyncFailed,
        isMaintenanceMode,
        syncedUserId,
        currentUserId: clerkUser?.id,
        needsSync: isClerkSignedIn && clerkUser && syncedUserId !== clerkUser.id
      });
      // @ts-expect-error -- window.__AUTH_STATE is an app-level global (written here, read in SignInPage)
      window.__AUTH_STATE = { isLoading, isSyncing, isSyncFailed, forceNotLoading, user: clerkUser, loadingReasons };
    }
  }, [isLoading, isAuthLoaded, isUserLoaded, isClerkSignedIn, clerkUser, isSyncing, isSyncFailed, isMaintenanceMode, forceNotLoading]);

  const signOut = useCallback(async () => {
    try {
      console.log('[Auth] Initiating sign out sequence...');
      
      // Clear refs and state immediately
      setSyncedUserId(null);
      setIsSyncing(false);
      setIsSyncFailed(false);
      
      // Clear all local app data
      queryClient.clear();
      sessionStorage.clear();
      localStorage.removeItem('active_org');
      
      if (clerkSignOut) {
        console.log('[Auth] Calling clerkSignOut()...');
        await clerkSignOut();
      } else {
        console.warn('[Auth] No clerkSignOut available, falling back to hard reload.');
      }
    } catch (error) {
      console.error('[Auth] Error during sign out process:', error);
    } finally {
      window.location.href = '/';
    }
  }, [clerkSignOut, queryClient]);

  const hardReset = useCallback(async () => {
    console.log('[Auth] Performing HARD RESET');
    
    // 1. Wipe everything
    setSyncedUserId(null);
    localStorage.clear();
    sessionStorage.clear();
    queryClient.clear();
    
    // 2. Clear Clerk if possible
    try {
      if (clerkSignOut) {
        console.log('[Auth] Calling Clerk signOut() for hard reset...');
        await clerkSignOut();
      }
    } catch (e) {
      console.error('[Auth] Hard reset Clerk error:', e);
    } finally {
      window.location.href = '/';
    }
  }, [clerkSignOut, queryClient]);

  const { data: dbProfile } = useQuery({
    queryKey: ['user-live-profile', clerkUser?.id],
    queryFn: async () => {
      try {
        const res = await apiWorker.get('/api/users/me');
        return (res.data && Array.isArray(res.data.content)) ? res.data.content[0] : (res.data?.content || res.data);
      } catch (err) {
        return null;
      }
    },
    enabled: !!isClerkSignedIn && isUserLoaded && !!clerkUser?.id && syncedUserId === clerkUser.id && !isSyncing && !isSyncFailed && !isEmailConflict,
    staleTime: 5 * 60 * 1000
  });

  const currentUser = React.useMemo(() => {
    if (!clerkUser) return null;
    
    // Extract role from dbProfile (Spring Boot), metadata, or clerkUser
    const dbRole = dbProfile?.globalRole || dbProfile?.role || dbProfile?.systemRole || dbProfile?.systemAdminRole;
    const metaRole = clerkUser.publicMetadata?.role || clerkUser.unsafeMetadata?.role;
    let rawRole = (dbRole || metaRole || (clerkUser as any).role || '').toString().trim();
    if (rawRole.toUpperCase().startsWith('ROLE_')) {
      rawRole = rawRole.substring(5);
    }
    const effectiveRole = rawRole.toUpperCase();

    // Check authorities array from Spring Security
    const authorities = dbProfile?.authorities || dbProfile?.roles || (clerkUser as any).authorities || [];
    const hasSuperAuthority = Array.isArray(authorities) && authorities.some((a: any) => {
      const authStr = (typeof a === 'string' ? a : (a?.authority || a?.name || a?.role || '')).toUpperCase();
      return (
        authStr === 'ROLE_SUPER_ADMIN' || authStr === 'SUPER_ADMIN' ||
        authStr === 'ROLE_SYSTEM_ADMIN' || authStr === 'SYSTEM_ADMIN' ||
        authStr === 'ROLE_SUPER_USER' || authStr === 'SUPER_USER' ||
        authStr === 'ROLE_SUPERUSER' || authStr === 'SUPERUSER'
      );
    });

    const isSuper = Boolean(
      effectiveRole === 'SUPER_ADMIN' ||
      effectiveRole === 'SYSTEM_ADMIN' ||
      effectiveRole === 'SUPER_USER' ||
      effectiveRole === 'SUPERUSER' ||
      dbProfile?.isSuperAdmin ||
      dbProfile?.superAdmin ||
      dbProfile?.isSuperUser ||
      dbProfile?.superUser ||
      dbProfile?.isSystemAdmin ||
      dbProfile?.systemAdmin ||
      clerkUser.publicMetadata?.isSuperAdmin ||
      clerkUser.unsafeMetadata?.isSuperAdmin ||
      clerkUser.publicMetadata?.superAdmin ||
      clerkUser.unsafeMetadata?.superAdmin ||
      clerkUser.publicMetadata?.isSuperUser ||
      clerkUser.unsafeMetadata?.isSuperUser ||
      clerkUser.publicMetadata?.superUser ||
      clerkUser.unsafeMetadata?.superUser ||
      hasSuperAuthority
    );

    return {
      ...clerkUser,
      ...(dbProfile || {}),
      id: clerkUser.id,
      userId: clerkUser.id,
      clerkId: clerkUser.id,
      dbId: dbProfile?.id,
      email: clerkUser.primaryEmailAddress?.emailAddress || (Array.isArray(clerkUser.emailAddresses) && clerkUser.emailAddresses[0]?.emailAddress) || dbProfile?.email,
      firstName: clerkUser.firstName || dbProfile?.firstName,
      lastName: clerkUser.lastName || dbProfile?.lastName,
      fullName: clerkUser.fullName || [clerkUser.firstName, clerkUser.lastName].filter(Boolean).join(' ') || dbProfile?.name,
      role: effectiveRole || (isSuper ? 'SUPER_ADMIN' : 'MEMBER'),
      isSuperAdmin: isSuper,
      superAdmin: isSuper,
      isSuperUser: isSuper,
      superUser: isSuper,
      publicMetadata: {
        ...(clerkUser.publicMetadata || {}),
        role: effectiveRole || (isSuper ? 'SUPER_ADMIN' : 'MEMBER'),
        isSuperAdmin: isSuper,
        isSuperUser: isSuper
      }
    };
  }, [clerkUser, dbProfile]);

  const isBackendSynced = Boolean(
    isClerkSignedIn && clerkUser?.id && syncedUserId === clerkUser.id && !isSyncFailed && !isSyncing
  );
  const isSignedIn = !!isClerkSignedIn;

  const value = React.useMemo(() => ({
    user: currentUser,
    memberships,
    isLoading,
    isSignedIn,
    isBackendSynced,
    isSyncSkipped,
    error,
    signOut,
    hardReset,
    getToken: (options?: any) => getToken ? getToken(options) : Promise.resolve(null),
    loginAs: async () => { console.warn('loginAs is only for mock mode'); },
    isSyncing,
    isSyncFailed,
    isMaintenanceMode,
    isCheckingHealth,
    retrySync,
    checkHealth,
    skipSync,
    enableGodMode,
    disableGodMode,
    isGodMode
  }), [currentUser, memberships, isLoading, isSignedIn, isBackendSynced, isSyncSkipped, error, isSyncing, isSyncFailed, isMaintenanceMode, isCheckingHealth, retrySync, checkHealth, signOut, skipSync, isGodMode, enableGodMode, disableGodMode]);

  return (
    <AuthCtx.Provider value={value}>
      {(isSyncFailed && !isMaintenanceMode) && (
        <div className={`fixed top-0 left-0 right-0 z-[70] ${isEmailConflict ? 'bg-[#FFD600] text-black' : 'bg-red-600 text-white'} px-4 md:px-8 py-3 border-b-4 border-black dark:border-white shadow-[0px_4px_0px_#000000] flex flex-col sm:flex-row items-center justify-between gap-3 animate-in fade-in slide-in-from-top duration-300`}>
          <div className="text-center sm:text-left">
            <p className="font-mono font-black uppercase text-xs tracking-wider">
              {isEmailConflict ? 'Account Conflict' : 'Profile Sync Delayed / Failed'}
            </p>
            <p className="text-[11px] font-mono mt-0.5 opacity-90">
              {isEmailConflict 
                ? 'Email already linked to another account. Please use original login provider.' 
                : "Unable to fully synchronize profile with servers. Some permissions may be using offline fallbacks."}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!isEmailConflict && (
              <button
                onClick={skipSync}
                className="px-3 py-1.5 bg-black/10 hover:bg-black/20 text-current text-xs font-mono font-black uppercase tracking-wider border-2 border-current rounded-none transition-colors"
              >
                Skip & Continue
              </button>
            )}
            <button 
              onClick={() => isEmailConflict ? signOut() : retrySync(false)}
              className="px-4 py-1.5 bg-black text-white dark:bg-white dark:text-black text-xs font-mono font-black uppercase tracking-wider rounded-none border-2 border-black dark:border-white hover:bg-[#0044FF] hover:text-white transition-colors shadow-[2px_2px_0px_#000000]"
            >
              {isEmailConflict ? 'Sign Out' : 'Retry Now'}
            </button>
          </div>
        </div>
      )}
      {children}
    </AuthCtx.Provider>
  );
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Demo sessions always use mock auth, even in production builds
  const isDemoSession = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('is_demo_session') === 'true';

  useEffect(() => {
    console.log(`[Auth] Initializing AuthProvider. SHOULD_USE_MOCKS: ${SHOULD_USE_MOCKS}, CLERK_KEY present: ${!!CLERK_PUBLISHABLE_KEY}, isDemoSession: ${isDemoSession}`);
  }, []);

  // Section 13: Hybrid Auth Delivery
  // If mocks are active OR we're in a demo session, use MockAuthProvider
  if (SHOULD_USE_MOCKS || isDemoSession) {
    console.log('[Auth] Routing to MockAuthProvider');
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }

  console.log('[Auth] Routing to LiveAuthProvider');
  return <LiveAuthProvider>{children}</LiveAuthProvider>;
};

export const useCurrentUser = () => {
  const context = useContext(AuthCtx);
  if (!context) throw new Error('useCurrentUser must be used within an AuthProvider');
  return context;
};
